import React from 'react';
import { Container, Card, Navbar } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import VersionFooter from '../components/VersionFooter';

export default function PrivacyPolicy() {
  const navigate = useNavigate();

  return (
    <div className="privacy-terms-page">
      <Navbar bg="light" expand="lg" className="shadow-sm">
        <Container>
          <Navbar.Brand 
            href="/" 
            onClick={(e) => { e.preventDefault(); navigate('/'); }}
            className="fw-bold fs-4"
            style={{ cursor: 'pointer' }}
          >
            <img src="/logo.svg" alt="trip147" style={{ width: '40px', height: '40px', marginRight: '10px' }} />
            trip147
          </Navbar.Brand>
        </Container>
      </Navbar>
      <Container className="pt-4 pb-5">
        <div className="mb-4">
          <h1 className="display-5 fw-bold mb-3">Privacy Policy</h1>
          <p className="text-muted">Last updated: November 2025</p>
        </div>

        <Card className="shadow-sm mb-4">
          <Card.Body className="p-4">
            <h2 className="h4 mb-3">1. Introduction</h2>
            <p>
              Welcome to trip147 ("we," "our," or "us"). We are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our trip planning service.
            </p>
            <p className="mb-0">
              <strong>Important:</strong> trip147 is currently in beta mode. This means the service is still under development and may experience bugs, downtime, or feature changes. By using our service, you acknowledge that you are using a beta version.
            </p>
          </Card.Body>
        </Card>

        <Card className="shadow-sm mb-4">
          <Card.Body className="p-4">
            <h2 className="h4 mb-3">2. Information We Collect</h2>
            
            <h3 className="h5 mt-3 mb-2">2.1 Personal Information</h3>
            <p>When you register for an account, we collect:</p>
            <ul>
              <li><strong>Email address</strong> - Required for account creation and communication</li>
              <li><strong>Name</strong> - Required for account identification</li>
              <li><strong>Password</strong> - Stored securely using industry-standard encryption</li>
            </ul>

            <h3 className="h5 mt-3 mb-2">2.2 Trip Data</h3>
            <p>When you use our service, we store:</p>
            <ul>
              <li>Trip plans and itineraries</li>
              <li>Daily activities and schedules</li>
              <li>Accommodation information</li>
              <li>Transportation details</li>
              <li>Location data</li>
              <li>Cost information</li>
              <li>To-do lists</li>
              <li>Packing lists</li>
              <li>Notes and additional information</li>
              <li>Images and links associated with activities</li>
            </ul>

            <h3 className="h5 mt-3 mb-2">2.3 Usage Data</h3>
            <p>We automatically collect certain information when you use our service:</p>
            <ul>
              <li>Account activity and login history</li>
              <li>Feature usage statistics</li>
              <li>Browser type and device information</li>
            </ul>
          </Card.Body>
        </Card>

        <Card className="shadow-sm mb-4">
          <Card.Body className="p-4">
            <h2 className="h4 mb-3">3. How We Use Your Information</h2>
            <p>We use the information we collect to:</p>
            <ul>
              <li>Provide, maintain, and improve our trip planning service</li>
              <li>Process your account registration and manage your account</li>
              <li>Send you service-related communications (e.g., password reset emails)</li>
              <li>Generate AI-powered trip suggestions and recommendations</li>
              <li>Fetch and display weather information for your trip destinations</li>
              <li>Retrieve images for activities and attractions</li>
              <li>Analyze service usage to improve functionality and user experience</li>
              <li>Ensure the security and integrity of our service</li>
            </ul>
          </Card.Body>
        </Card>

        <Card className="shadow-sm mb-4">
          <Card.Body className="p-4">
            <h2 className="h4 mb-3">4. Third-Party Services</h2>
            <p>We use third-party services to provide certain features of our service, including:</p>
            <ul>
              <li>AI services for generating trip recommendations</li>
              <li>Email services for sending transactional emails</li>
              <li>Analytics services to understand how our service is used</li>
              <li>Image services to provide images for activities and attractions</li>
              <li>Weather services to provide weather forecasts</li>
            </ul>
            <p className="mt-3 mb-0">
              These third-party services have their own privacy policies. We encourage you to review their policies to understand how they handle your data.
            </p>
          </Card.Body>
        </Card>

        <Card className="shadow-sm mb-4">
          <Card.Body className="p-4">
            <h2 className="h4 mb-3">5. Data Storage and Security</h2>
            <p>We implement industry-standard security measures to protect your data:</p>
            <ul>
              <li>Passwords are encrypted before storage</li>
              <li>Secure authentication methods are used</li>
              <li>Data is stored in secure, encrypted databases</li>
            </ul>
            <p className="mt-3 mb-0">
              While we implement security measures, no method of transmission over the Internet or electronic storage is 100% secure. We cannot guarantee absolute security of your data.
            </p>
          </Card.Body>
        </Card>

        <Card className="shadow-sm mb-4">
          <Card.Body className="p-4">
            <h2 className="h4 mb-3">6. Data Sharing</h2>
            <p><strong>We do not sell your personal information.</strong></p>
            
            <h3 className="h5 mt-3 mb-2">6.1 Shared Trip Plans</h3>
            <p>When you share a trip plan using our sharing feature:</p>
            <ul>
              <li>Private information is automatically removed, including:
                <ul>
                  <li>All cost information</li>
                  <li>To-do lists</li>
                  <li>Packing lists</li>
                  <li>Private notes</li>
                  <li>Private links</li>
                </ul>
              </li>
              <li>Only itinerary information (activities, locations, dates) is shared</li>
              <li>Shared plans expire automatically after a set period</li>
            </ul>

            <h3 className="h5 mt-3 mb-2">6.2 Service Providers</h3>
            <p>We may share your information with third-party service providers solely for the purpose of providing our service. These providers are contractually obligated to protect your information.</p>
          </Card.Body>
        </Card>

        <Card className="shadow-sm mb-4">
          <Card.Body className="p-4">
            <h2 className="h4 mb-3">7. Your Rights</h2>
            <p>You have the right to:</p>
            <ul>
              <li><strong>Access</strong> - Request access to your personal data stored in our system</li>
              <li><strong>Correction</strong> - Update or correct your personal information through your account settings</li>
              <li><strong>Deletion</strong> - Request deletion of your account and all associated data</li>
              <li><strong>Export</strong> - Export your trip data in standard formats</li>
              <li><strong>Withdrawal</strong> - Withdraw consent for data processing (note: this may limit service functionality)</li>
            </ul>
            <p className="mt-3 mb-0">
              To exercise these rights, please contact us using the information provided at the end of this policy.
            </p>
          </Card.Body>
        </Card>

        <Card className="shadow-sm mb-4">
          <Card.Body className="p-4">
            <h2 className="h4 mb-3">8. Data Retention</h2>
            <p>We retain your personal information for as long as your account is active or as needed to provide you with our service. If you delete your account:</p>
            <ul>
              <li>Your account information will be deleted</li>
              <li>All associated trip plans and data will be permanently removed</li>
              <li>Shared plans will remain accessible until they expire</li>
            </ul>
            <p className="mt-3 mb-0">
              We may retain certain information for a limited period to comply with legal obligations or resolve disputes.
            </p>
          </Card.Body>
        </Card>

        <Card className="shadow-sm mb-4">
          <Card.Body className="p-4">
            <h2 className="h4 mb-3">9. Cookies and Tracking</h2>
            <p>We use cookies and similar technologies to:</p>
            <ul>
              <li>Maintain your login session</li>
              <li>Store your preferences</li>
              <li>Collect anonymous usage statistics</li>
            </ul>
            <p className="mt-3">
              <strong>Cookie Consent:</strong> By logging into our service, you agree to our use of cookies as described in this Privacy Policy. Cookies are essential for maintaining your login session and providing a secure, personalized experience. If you do not wish to accept cookies, you may control them through your browser settings; however, this may affect the functionality of our service and prevent you from logging in.
            </p>
            <p className="mb-0">
              You can control cookies through your browser settings. However, disabling cookies may affect the functionality of our service.
            </p>
          </Card.Body>
        </Card>

        <Card className="shadow-sm mb-4">
          <Card.Body className="p-4">
            <h2 className="h4 mb-3">10. Children's Privacy</h2>
            <p>
              Our service is not intended for children under the age of 13. We do not knowingly collect personal information from children under 13. If you believe we have collected information from a child under 13, please contact us immediately.
            </p>
          </Card.Body>
        </Card>

        <Card className="shadow-sm mb-4">
          <Card.Body className="p-4">
            <h2 className="h4 mb-3">11. International Data Transfers</h2>
            <p>
              Your information may be transferred to and processed in countries other than your country of residence. These countries may have data protection laws that differ from those in your country. By using our service, you consent to the transfer of your information to these countries.
            </p>
          </Card.Body>
        </Card>

        <Card className="shadow-sm mb-4">
          <Card.Body className="p-4">
            <h2 className="h4 mb-3">12. Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new Privacy Policy on this page and updating the "Last updated" date. You are advised to review this Privacy Policy periodically for any changes.
            </p>
          </Card.Body>
        </Card>

        <Card className="shadow-sm mb-4">
          <Card.Body className="p-4">
            <h2 className="h4 mb-3">13. Contact Us</h2>
            <p>
              If you have any questions about this Privacy Policy or wish to exercise your rights regarding your personal data, please contact us:
            </p>
            <p className="mb-0">
              <strong>Email:</strong> gil.trip147@gmail.com<br />
              <strong>Service:</strong> trip147<br />
              <strong>Website:</strong> <a href="https://trip147.com/">https://trip147.com/</a>
            </p>
          </Card.Body>
        </Card>
      </Container>
      <VersionFooter />
    </div>
  );
}
